import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  OnInit,
  Output,
  EventEmitter,
  signal,
  computed,
  inject
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { HttpStatusCode } from '@angular/common/http';
import { Subject, forkJoin, takeUntil, debounceTime, distinctUntilChanged, Observable } from 'rxjs';

// Servicios
import { StoreV3Service } from '../../../services/api/v1/store-v3.service';
import { GeolocationV3Service } from '../../../services/geolocation-v3.service';
import { RequestService } from '../../../services/api/v1/request.service';
import { UtilService, CustomMessageService } from 'library';
import { OriginMessageError } from 'library';

// Interfaces
import {StoreV3, StoreCategoryV3, OfficeV3, StoreFilterV3, DistanceOptionV3, RegionOptionV3, ComponentStateV3} from '../../../interfaces';
// Enums
import { SalesWorkflow, SalesWorkflowSteps } from '../../../enums';
// Environment
import { environment } from '../../../../environments/environment';

/**
 * Componente principal de Store List
 * Contiene toda la lógica común para V2 y V3
 * Actúa como container que renderiza la versión correcta según el flujo
 */
@Component({
  selector: 'app-sales-store-list',
  templateUrl: './sales-store-list.component.html',
})
export class SalesStoreListComponent implements AfterViewInit, OnDestroy {

  protected readonly storeService = inject(StoreV3Service);
  protected readonly geolocationService = inject(GeolocationV3Service);
  protected readonly requestService = inject(RequestService);
  protected readonly utilService = inject(UtilService);
  protected readonly customMessageService = inject(CustomMessageService);
  protected readonly route = inject(ActivatedRoute);
  protected readonly fb = inject(FormBuilder);

  @Output() nextStepEvent = new EventEmitter<any>();
  @Output() gotoStepEvent = new EventEmitter<any>();
  form!: FormGroup;
  searchControl = new FormControl('');

  readonly preApprovalAmount = signal<number>(0);
  readonly currency = environment.global.country.currency;
  readonly allStores = signal<StoreV3[]>([]);
  readonly filteredStores = signal<StoreV3[]>([]);
  readonly displayedStores = signal<StoreV3[]>([]);
  readonly allCategories = signal<StoreCategoryV3[]>([]);

  // --- Estado de UI ---
  readonly selectedCategoryCode = signal<string | null>(null);
  readonly componentState = signal<ComponentStateV3>('loading');
  readonly isLoadingMore = signal<boolean>(false);
  readonly activeFiltersCount = signal<number>(0);

  readonly selectedStore = signal<StoreV3 | null>(null);
  readonly nearbyOffices = signal<OfficeV3[]>([]);
  readonly selectedOffice = signal<OfficeV3 | null>(null);

  // ============================================================
  // COMPUTED - Valores derivados
  // ============================================================
  readonly totalStores = computed(() => this.filteredStores().length);

  readonly hasLocation = computed(() => this.geolocationService.hasLocation());
  readonly hasMoreStores = computed(() => {
    return this.displayedStores().length < this.filteredStores().length;
  });

  // ============================================================
  // VIEWCHILD - Referencias a elementos del DOM
  // ============================================================

  @ViewChild('stickyTrigger') stickyTrigger!: ElementRef<HTMLElement>;
  @ViewChild('storeListContainer') storeListContainer!: ElementRef<HTMLElement>;

  // ============================================================
  // VARIABLES DE ESTADO - UI
  // ============================================================

  // Scroll sticky
  isScrolled = false;
  private observer!: IntersectionObserver;
  private scrollObserver!: IntersectionObserver;
  private readonly stickyOffset = 120;

  isLoading = false;

  // Drag de categorías
  isDragging = false;
  hasDragged = false;
  startX = 0;
  scrollLeft = 0;
  private readonly dragThreshold = 5;

  // Modales
  showStoreInfo = false;
  showFilters = false;

  // Paginación (scroll infinito)
  private readonly pageSize = 12;
  private currentPage = 1;

  // Opciones para filtros
  distanceOptions: DistanceOptionV3[] = [];
  regionOptions: RegionOptionV3[] = [];

  // Limpieza de subscripciones
  protected destroy$ = new Subject<void>();

  // ID de la solicitud
  protected requestId: string = this.route.snapshot.params['request_id'];

  // Flow code
  protected flowCode: string = this.route.snapshot.params['flow_code'];

  // Constante para el código de atributo de pre-selección
  private readonly ATTRIBUTE_CODE_PRESELECCION = 'informacion_pre_seleccion_cliente';

  /**
   * Determina si el flujo actual es Krdya (V3)
   */
  get isFlowKrdya(): boolean {
    return this.flowCode === SalesWorkflow['app-krdya-cliente'] || this.flowCode === SalesWorkflow['app-krdya-kredicash'];
  }

  ngOnInit() {
    // Container: la inicialización se delega a los hijos (V2/V3)
  }

  // ============================================================
  // LIFECYCLE HOOKS
  // ============================================================

  ngAfterViewInit(): void {
    // Container: la inicialización de observers se delega a los hijos (V2/V3)
  }

  ngOnDestroy(): void {
    // Limpiar observers
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }

    // Limpiar subscripciones
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================================
  // INICIALIZACIÓN
  // ============================================================

  /**
   * Inicializa el formulario de filtros
   */
  initForm(): void {
    this.form = this.fb.group({
      distance: [null],
      regions: [null]
    });
  }

  /**
   * Configura el listener de búsqueda con debounce
   * Espera 400ms después de que el usuario deje de escribir
   */
  initSearchListener(): void {
    this.searchControl.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.applyLocalFilters();
    });
  }

  /**
   * Inicializa las opciones de los filtros
   */
  initFilterOptions(): void {
    // Opciones de distancia
    this.distanceOptions = [
      { id: '1', name: 'Hasta 5 Km.', value: 5 },
      { id: '2', name: '5 Km. a 10 Km.', value: 10 },
      { id: '3', name: '10 Km. a 15 Km.', value: 15 },
      { id: '4', name: 'Más de 15 Km.', value: 100 }
    ];

    // Opciones de región (se pueden cargar del API si es necesario)
    this.regionOptions = [
      { id: '1', name: 'Panamá', value: 'panama' },
      { id: '2', name: 'Panamá Oeste', value: 'panama-oeste' },
      { id: '3', name: 'Colón', value: 'colon' },
      { id: '4', name: 'Chiriquí', value: 'chiriqui' },
      { id: '5', name: 'Veraguas', value: 'veraguas' },
      { id: '6', name: 'Herrera', value: 'herrera' },
      { id: '7', name: 'Los Santos', value: 'los-santos' },
      { id: '8', name: 'Coclé', value: 'cocle' },
      { id: '9', name: 'Bocas del Toro', value: 'bocas-del-toro' },
      { id: '10', name: 'Darién', value: 'darien' }
    ];
  }

  /**
   * Carga los datos iniciales del componente
   */
  loadInitialData(): void {
    this.componentState.set('loading');
    this.utilService.enablePageLoading();

    // Primero solicitar geolocalización
    this.geolocationService.requestPermission().subscribe(() => {
      // Obtener coordenadas de geolocalización
      const location = this.geolocationService.location();
      const latitude = location?.latitude;
      const longitude = location?.longitude;

      // Luego cargar pre-aprobación y tiendas en paralelo
      forkJoin([
        this.storeService.getPreApproval(this.requestId),
        this.storeService.getStores(undefined, latitude, longitude)
      ]).subscribe({
        next: ([preApprovalResp, storesResp]) => {
          // Procesar pre-aprobación
          if (preApprovalResp.status === HttpStatusCode.Ok) {
            this.preApprovalAmount.set(preApprovalResp.body.data.amount);
            this.storeService.store["preApprovalAmount"] = preApprovalResp.body.data.amount; // Guardar en servicio para acceso global
          }

          // Procesar tiendas
          if (storesResp.status === HttpStatusCode.Ok) {
            const stores = storesResp.body.data.list;
            this.allStores.set(stores);

            // Extraer categorías únicas de las tiendas
            const categories = this.extractUniqueCategories(stores);
            this.allCategories.set(categories);

            // Seleccionar primera categoría por defecto (RF-01)
            if (categories.length > 0) {
              this.selectedCategoryCode.set(categories[0].code);
              // Enviar category_id de la categoría por defecto al backend
              this.updateCategoryInRequest(categories[0].code);
            }

            // Aplicar filtros iniciales
            this.applyLocalFilters();
          }

          this.utilService.disablePageLoading();
        },
        error: (error) => {
          console.error('[SalesStoreListV3] Error cargando datos:', error);
          this.componentState.set('error');
          this.utilService.disablePageLoading();
          this.customMessageService.responseNoticeHandler(error, OriginMessageError.BACKEND);
        }
      });
    });
  }

  /**
   * Extrae categorías únicas de todas las tiendas
   * Las tiendas vienen con sus categorías embebidas
   */
  private extractUniqueCategories(stores: StoreV3[]): StoreCategoryV3[] {
    const categoryMap = new Map<string, StoreCategoryV3>();

    // Recorrer todas las tiendas y sus categorías
    stores.forEach(store => {
      // Categorías del store
      store.categories?.forEach(category => {
        if (!categoryMap.has(category.code)) {
          categoryMap.set(category.code, category);
        }
      });

      // Categorías de las offices
      store.offices?.forEach(office => {
        office.categories?.forEach(category => {
          if (!categoryMap.has(category.code)) {
            categoryMap.set(category.code, category);
          }
        });
      });
    });

    // Convertir Map a Array y ordenar por nombre
    return Array.from(categoryMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }

  // ============================================================
  // FILTRADO LOCAL (Categoría y Búsqueda)
  // ============================================================

  /**
   * Aplica los filtros locales (categoría y búsqueda por nombre)
   * Este método se llama cada vez que cambia la categoría o el término de búsqueda
   */
  applyLocalFilters(): void {
    let stores = this.allStores();

    // FILTRO 1: Por categoría seleccionada
    // Busca en store.categories y también en store.offices[].categories
    const categoryCode = this.selectedCategoryCode();
    if (categoryCode) {
      stores = stores.filter(store => {
        // Buscar en categorías del store
        const inStoreCategories = store.categories?.some(cat => cat.code === categoryCode);
        if (inStoreCategories) return true;

        // Buscar en categorías de las offices
        const inOfficeCategories = store.offices?.some(office =>
          office.categories?.some(cat => cat.code === categoryCode)
        );
        return inOfficeCategories;
      });
    }

    // FILTRO 2: Por término de búsqueda (nombre, case-insensitive)
    const searchTerm = this.searchControl.value?.toLowerCase().trim();
    if (searchTerm) {
      stores = stores.filter(store =>
        store.name.toLowerCase().includes(searchTerm)
      );
    }

    // Ordenar alfabéticamente por nombre
    stores.sort((a, b) => a.name.localeCompare(b.name));

    // Guardar tiendas filtradas
    this.filteredStores.set(stores);

    // Resetear paginación y mostrar primera página
    this.currentPage = 1;
    this.displayedStores.set(stores.slice(0, this.pageSize));

    // Actualizar estado del componente
    this.componentState.set(stores.length > 0 ? 'loaded' : 'empty');
  }

  /**
   * Selecciona una categoría
   * @param categoryCode - Código de la categoría a seleccionar
   */
  selectCategory(categoryCode: string): void {
    // Si estaba arrastrando, no seleccionar
    if (this.hasDragged) return;

    // Si es la misma categoría, no hacer nada
    if (this.selectedCategoryCode() === categoryCode) return;

    // Cambiar categoría y aplicar filtros
    this.selectedCategoryCode.set(categoryCode);
    this.applyLocalFilters();

    // Llamar al PATCH /workable con el category_id
    this.updateCategoryInRequest(categoryCode);
  }

  /**
   * Actualiza el category_id en la solicitud via PATCH /workable
   * @param categoryCode - Código de la categoría seleccionada
   */
  private updateCategoryInRequest(categoryCode: string): void {
    const category = this.allCategories().find(cat => cat.code === categoryCode);
    if (!category?.id) return;

    this.requestService.update(this.requestId, { category_id: category.id }).subscribe({
      next: () => {
        console.log('[SalesStoreListV3] category_id actualizado:', category.id);
      },
      error: (error) => {
        console.error('[SalesStoreListV3] Error actualizando category_id:', error);
      }
    });
  }

  // ============================================================
  // FILTROS BACKEND (Distancia y Región)
  // ============================================================

  /**
   * Aplica los filtros del modal (distancia y región)
   * Estos filtros requieren una nueva llamada al API
   */
  applyBackendFilters(): void {
    this.showFilters = false;
    this.utilService.enablePageLoading();

    // Construir filtros para el API
    const filters: StoreFilterV3 = {};

    const distanceOption = this.form.get('distance')?.value as DistanceOptionV3;
    if (distanceOption) {
      filters.maxDistance = distanceOption.value;
    }

    const regionOption = this.form.get('regions')?.value as RegionOptionV3;
    if (regionOption) {
      filters.administrativeRegions = regionOption.value;
    }

    // Actualizar contador de filtros activos
    this.updateActiveFiltersCount();

    // Obtener coordenadas de geolocalización
    const location = this.geolocationService.location();
    const latitude = location?.latitude;
    const longitude = location?.longitude;

    // Llamar al API con los nuevos filtros
    this.storeService.getStores(filters, latitude, longitude).subscribe({
      next: (resp) => {
        if (resp.status === HttpStatusCode.Ok) {
          const stores = resp.body.data.list;
          this.allStores.set(stores);

          // Re-extraer categorías (pueden haber cambiado)
          const categories = this.extractUniqueCategories(stores);
          this.allCategories.set(categories);

          // Re-aplicar filtros locales
          this.applyLocalFilters();
        }
        this.utilService.disablePageLoading();
      },
      error: (error) => {
        console.error('[SalesStoreListV3] Error aplicando filtros:', error);
        this.utilService.disablePageLoading();
        this.customMessageService.responseNoticeHandler(error, OriginMessageError.BACKEND);
      }
    });
  }

  /**
   * Limpia todos los filtros y recarga las tiendas
   */
  clearFilters(): void {
    // Limpiar formulario de filtros
    this.form.reset();

    // Limpiar búsqueda
    this.searchControl.reset();

    // Resetear contador de filtros
    this.activeFiltersCount.set(0);

    // Cerrar modal si está abierto
    this.showFilters = false;

    // Recargar tiendas sin filtros
    this.loadInitialData();
  }

  /**
   * Actualiza el contador de filtros activos
   */
  private updateActiveFiltersCount(): void {
    let count = 0;

    if (this.form.get('distance')?.value) count++;
    if (this.form.get('regions')?.value) count++;
    if (this.searchControl.value) count++;

    this.activeFiltersCount.set(count);
  }

  // ============================================================
  // SCROLL INFINITO
  // ============================================================

  /**
   * Configura el observer para scroll infinito
   */
  protected initScrollObserver(): void {
    // Crear elemento sentinel al final de la lista
    const container = this.storeListContainer?.nativeElement;
    if (!container) return;

    const sentinel = document.createElement('div');
    sentinel.id = 'scroll-sentinel';
    sentinel.style.height = '1px';
    container.appendChild(sentinel);

    // Crear observer
    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !this.isLoadingMore() && this.hasMoreStores()) {
          this.loadMoreStores();
        }
      },
      { threshold: 0.1 }
    );

    this.scrollObserver.observe(sentinel);
  }

  /**
   * Carga más tiendas para scroll infinito
   */
  loadMoreStores(): void {
    if (this.isLoadingMore() || !this.hasMoreStores()) return;

    this.isLoadingMore.set(true);

    // Simular delay de red
    setTimeout(() => {
      this.currentPage++;
      const allFiltered = this.filteredStores();
      const endIndex = this.currentPage * this.pageSize;

      // Agregar más tiendas a las mostradas
      this.displayedStores.set(allFiltered.slice(0, endIndex));
      this.isLoadingMore.set(false);
    }, 300);
  }

  // ============================================================
  // SELECCIÓN DE TIENDA
  // ============================================================

  /**
   * Maneja la selección de una tienda
   * Abre el modal y carga las sucursales cercanas
   */
  onSelectStore(store: StoreV3): void {
    this.selectedStore.set(store);
    this.showStoreInfo = true;

    // Siempre llamar al endpoint de sucursales cercanas
    this.loadNearbyOffices(store);
  }

  /**
   * Carga las sucursales cercanas desde el API
   * Siempre llama al endpoint nearby, si no devuelve resultados usa fallback
   */
  private loadNearbyOffices(store: StoreV3): void {
    const location = this.geolocationService.location();
    const latitude = location?.latitude;
    const longitude = location?.longitude;

    this.utilService.enablePageLoading();

    this.storeService.getNearbyOffices(store.id, latitude, longitude).subscribe({
      next: (resp) => {
        if (resp.status === HttpStatusCode.Ok) {
          const offices = resp.body.data.list;

          // Si el nearby devuelve oficinas y tienen address, usarlas
          if (offices && offices.length > 0 && offices[0].address) {
            // Si la oficina no tiene min_installment, usar el de la tienda
            const enrichedOffices = offices.map((office: OfficeV3) => ({
              ...office,
              min_installment: office.min_installment ?? store.min_installment
            }));
            this.nearbyOffices.set(enrichedOffices);
            this.selectedOffice.set(enrichedOffices[0]);
            this.storeService.store["selectedOffice"] = enrichedOffices[0]; // Guardar en servicio para acceso global
          } else {
            // Si no devuelve oficinas o no tienen address, usar fallback
            this.setFallbackOffice(store);
          }
        } else {
          // Si la respuesta no es OK, usar fallback
          this.setFallbackOffice(store);
        }
        this.utilService.disablePageLoading();
      },
      error: (error) => {
        console.error('[SalesStoreListV3] Error cargando sucursales:', error);
        // Fallback: usar la dirección de la tienda
        this.setFallbackOffice(store);
        this.utilService.disablePageLoading();
        this.customMessageService.responseNoticeHandler(error, OriginMessageError.BACKEND);
      }
    });
  }

  /**
   * Configura una oficina de fallback usando los datos de la tienda
   */
  private setFallbackOffice(store: StoreV3): void {
    const fallbackOffice: OfficeV3 = {
      id: store.id,
      name: store.name,
      address: store.address,
      phone: store.phone,
      region: store.region,
      zone: store.zone,
      distance: store.distance,
      min_installment: store.min_installment
    };
    this.selectedOffice.set(fallbackOffice);
    this.storeService.store["selectedOffice"] = fallbackOffice; // Guardar en servicio para acceso global
    this.nearbyOffices.set([fallbackOffice]);
  }

  /**
   * Cierra el modal de información de tienda
   */
  closeStoreInfo(): void {
    this.showStoreInfo = false;
    // NO limpiamos selectedStore para mantener estado si se reabre
  }

  // ============================================================
  // NAVEGACIÓN
  // ============================================================

  /**
   * Guarda los atributos de pre-selección del cliente
   */
  private savePreSelectionAttributes(): Observable<any> {
    const store = this.selectedStore();
    const office = this.selectedOffice();

    const body = {
      franquicia: store ? {
        id: store.id,
        nombre: store.name,
        direccion: store.address,
        telefono: store.phone
      } : null,
      tienda: office ? {
        id: office.id,
        nombre: office.name,
        direccion: office.address,
        telefono: office.phone
      } : null,
      monto_maximo_aprobado: this.preApprovalAmount()
    };

    return this.requestService.saveAttributes(
      this.requestId,
      this.ATTRIBUTE_CODE_PRESELECCION,
      body
    );
  }

  /**
   * Confirma la selección de tienda y navega al siguiente paso
   * Si la categoría seleccionada es kredicash, cambia al flujo app-krdya-kredicash
   */
  onConfirmStore(): void {
    const store = this.selectedStore();
    const office = this.selectedOffice();
    const categoryCode = this.selectedCategoryCode();

    if (!store || !office) return;

    this.isLoading = true;
    this.utilService.enablePageLoading();

    // Primero guardar atributos de pre-selección (bloqueante para todas las categorías)
   this.savePreSelectionAttributes().subscribe({
      next: () => {
        if (categoryCode === 'kredicash') {
          const data: any = {
            store: { id: store.id, name: store.name }
          };

          this.requestService.update(this.requestId, data).subscribe({
            next: (resp) => {
              if (resp.status === HttpStatusCode.NoContent || resp.status === HttpStatusCode.Ok) {
                this.showStoreInfo = false;
                const kredicashCategory = this.allCategories().find(cat => cat.code === 'kredicash');

                this.gotoStepEvent.emit({
                  flowCode: SalesWorkflow['app-krdya-kredicash'],
                  stepCode: SalesWorkflowSteps['simulator'],
                  categoryId: kredicashCategory?.id,
                  categoryCode: 'kredicash'
                });
              } else {
                this.customMessageService.responseNoticeHandler(resp, OriginMessageError.BACKEND);
              }
              this.isLoading = false;
              this.utilService.disablePageLoading();
            },
            error: (error) => {
              console.error('[SalesStoreList] Error confirmando tienda:', error);
              this.isLoading = false;
              this.utilService.disablePageLoading();
              this.customMessageService.responseNoticeHandler(error, OriginMessageError.BACKEND);
            }
          });
        } else {
          // Para otras categorías, navegar directo
          this.showStoreInfo = false;
          this.nextStepEvent.emit({ store: { id: store.id, name: store.name } });
          this.isLoading = false;
          this.utilService.disablePageLoading();
        }
      },
      error: (error) => {
        console.error('[SalesStoreList] Error guardando atributos:', error);
        this.isLoading = false;
        this.utilService.disablePageLoading();
        this.customMessageService.responseNoticeHandler(error, OriginMessageError.BACKEND);
      }
    });
  }

  // ============================================================
  // SCROLL STICKY
  // ============================================================

  /**
   * Configura el observer para detectar cuando hacer sticky el header
   */
  protected initStickyObserver(): void {
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isScrolled = !entry.isIntersecting;
      },
      {
        root: null,
        threshold: 0,
        rootMargin: `-${this.stickyOffset}px 0px 0px 0px`
      }
    );

    this.observer.observe(this.stickyTrigger.nativeElement);
  }

  // ============================================================
  // DRAG DE CATEGORÍAS
  // ============================================================

  onMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    this.hasDragged = false;
    this.startX = event.pageX;
    this.scrollLeft = (event.currentTarget as HTMLElement).scrollLeft;
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;

    const container = event.currentTarget as HTMLElement;
    const deltaX = event.pageX - this.startX;

    if (Math.abs(deltaX) > this.dragThreshold) {
      this.hasDragged = true;
      event.preventDefault();
    }

    if (this.hasDragged) {
      container.scrollLeft = this.scrollLeft - deltaX * 1.2;
    }
  }

  onMouseUp(): void {
    this.isDragging = false;
  }

  // ============================================================
  // UTILIDADES
  // ============================================================

  /**
   * Abre el modal de filtros
   */
  onShowFilters(): void {
    this.showFilters = true;
  }

  onNextStep(): void {
    this.nextStepEvent.emit();
  }
}
